import { Link } from "react-router-dom";
import { Building2, Users, Rocket, ArrowRight } from "lucide-react";
import { SectionHeader } from "@/components/sections/SectionHeader";

const clientTypes = [
  {
    icon: Building2,
    label: "Owner-Led SMBs",
    revenue: "$1M – $25M Revenue",
    description: "You've built something real — but growth is creating friction.",
    challenges: [
      "Margin leakage you can't pinpoint",
      "Teams working hard but not aligned",
      "Tools everywhere, visibility nowhere",
    ],
    deliverables: [
      "Operational clarity",
      "Decision-grade reporting",
      "Scalable systems built for AI disruption",
    ],
  },
  {
    icon: Users,
    label: "Family Businesses",
    revenue: "2nd & 3rd Generation",
    description: "Legacy built the business. Informality is now holding it back.",
    challenges: [
      "Blurred governance",
      "Emotional decision-making",
      "Resistance to modernization",
    ],
    deliverables: [
      "Structured controls without bureaucracy",
      "AI-assisted oversight",
      "Governance that respects legacy and ensures longevity",
    ],
  },
  {
    icon: Rocket,
    label: "Micro & Early-Growth",
    revenue: "$100k – $2M ARR",
    description: "The business is moving faster than the systems supporting it.",
    challenges: [
      "Founder overload",
      "No operating rhythm",
      "Tech decisions without strategy",
    ],
    deliverables: [
      "A structured operating backbone",
      "Strategic prioritization",
      "Leadership bandwidth through advisory",
    ],
  },
];

export const WhoWeWorkWithSection = () => {
  return (
    <section className="section-padding bg-secondary">
      <div className="section-container">
        <SectionHeader
          eyebrow="Who We Work With"
          title="Businesses That Power the Economy"
          subtitle="We partner with owners and leaders who are serious about building something that lasts."
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          {clientTypes.map((client, index) => (
            <div
              key={index}
              className="bg-background border border-border p-8 flex flex-col"
            >
              {/* Header */}
              <div className="mb-6">
                <client.icon size={32} className="text-foreground mb-4" />
                <h3 className="heading-4 mb-1">{client.label}</h3>
                <p className="font-sans text-sm text-muted-foreground font-medium">
                  {client.revenue}
                </p>
              </div>

              <p className="body-base text-muted-foreground mb-6">
                {client.description}
              </p>

              {/* Challenges */}
              <div className="mb-6">
                <p className="font-sans text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                  Common Challenges
                </p>
                <ul className="space-y-2">
                  {client.challenges.map((challenge, i) => (
                    <li key={i} className="font-sans text-sm text-foreground flex items-start gap-2">
                      <span className="text-muted-foreground mt-1">•</span>
                      {challenge}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Deliverables */}
              <div className="mt-auto pt-6 border-t border-border">
                <p className="font-sans text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                  What We Deliver
                </p>
                <ul className="space-y-2">
                  {client.deliverables.map((deliverable, i) => (
                    <li key={i} className="font-sans text-sm text-foreground flex items-start gap-2">
                      <span className="text-primary mt-1">→</span>
                      {deliverable}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center">
          <Link
            to="/who-we-work-with"
            className="font-sans text-sm font-medium text-foreground hover:text-muted-foreground transition-colors inline-flex items-center gap-2"
          >
            Learn more about who we serve
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
};
